// @flow
/* eslint-disable no-underscore-dangle */
import {fromJS, List, Map, Set} from 'immutable';
import {getStoreByName, Store} from 'client/framework';
import teacherClassroomSettingsActions from './TeacherClassroomSettingsActions';
import {history} from 'client/history';
import appStore from 'client/AppStore';
import {resource, type QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import BlockyDropdownStore from 'generic/BlockyDropdown/BlockyDropdownStore';
import classroomsStore from 'client/Classrooms/ClassroomsStore';

import {getClassroomsForTeacherClassroomsDashboardQuery} from 'client/Classrooms/Teacher/ClassroomsList/helpers';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {SubjectModelV2} from 'resources/augmented/Subject/SubjectModel.v2';

const CLASSROOM_SCHOOL_SELECTION_DROPDOWN_STORE_NAME = 'classroomSchoolSelectionDropdownStore';

class TeacherClassroomSettingsStore extends Store {
  constructor(name: string) {
    super(name);
    this.initialData = fromJS({
      editedSelectedSubjects: false,
      classroomName: '',
      successMessaging: '',
      errorMessaging: '',
      formErrors: {},
      hasUnapprovedCoteacherError: false,
      // Validation kicks in when this is true -- only when save has been clicked
      saveAttempted: false,
      isInitialized: false,
      selectedSubjects: []
    });
    this.setInitialData(this.initialData);

    const schoolSelectionDropdownStore = getStoreByName(
      CLASSROOM_SCHOOL_SELECTION_DROPDOWN_STORE_NAME
    );
    this._schoolSelectionDropdownStore =
      schoolSelectionDropdownStore ||
      new BlockyDropdownStore(CLASSROOM_SCHOOL_SELECTION_DROPDOWN_STORE_NAME);

    this.handle(teacherClassroomSettingsActions.ARCHIVE_CLASSROOM, this._archiveClassroom);
    this.handle(teacherClassroomSettingsActions.UNARCHIVE_CLASSROOM, this._unarchiveClassroom);
    this.handle(teacherClassroomSettingsActions.DELETE_CLASSROOM, this._deleteClassroom);
    this.handle(teacherClassroomSettingsActions.SAVE_CLASSROOM, this._saveClassroom);
    this.handle(teacherClassroomSettingsActions.SET_CLASS_NAME, this.setProperty('classroomName'));
    this.handle(teacherClassroomSettingsActions.MESSAGE_SHOWN, this._removeMessaging);
    this.handle(teacherClassroomSettingsActions.REMOVE_SELF_FROM_CLASSROOM, this._removeSelfFromClassroom)
    this.handle(teacherClassroomSettingsActions.RESET_STORE, this.resetStore);
    this.handle(teacherClassroomSettingsActions.INITIALIZE_SETTINGS, this._initializeSettings);
    this.handle(
      teacherClassroomSettingsActions.SCHOOL_ASSOCIATION_CHANGE,
      this._handleSchoolAssociationChange
    );
    this.handle(
      teacherClassroomSettingsActions.SET_SELECTED_SUBJECTS,
      this.setProperty('selectedSubjects')
    );
  }

  getSelectedSubjects(): List<SubjectModelV2> {
    return this.readData('selectedSubjects');
  }

  _initializeSettings(classroomName: string) {
    this.writeData('isInitialized', true);
    this.writeData('classroomName', classroomName);
  }

  isInitialized(): boolean {
    return this.readData('isInitialized');
  }

  async _archiveClassroom(): Promise<any> {
    const classroom = this.getClassroom().archiveClassroom();

    try {
      await classroom.save();

      this.writeData('successMessaging', 'Your classroom has been archived');
      classroomsStore.getTeacherActiveClassroomsQuery().invalidateInterest();
      classroomsStore.getActiveClassroomMetadataQuery().invalidateInterest();
      classroomsStore.getTeacherArchivedClassroomsQuery().invalidateInterest();
      const {resourcePath} = getClassroomsForTeacherClassroomsDashboardQuery().done();
      invalidatePartialInterest({resourcePath});
    } catch (e) {
      this.writeData('errorMessaging', 'There was a problem archiving your classroom');
    }
  }

  async _unarchiveClassroom(): Promise<any> {
    const classroom = this.getClassroom().unarchiveClassroom();

    try {
      await classroom.save();
      this.writeData('successMessaging', 'Your classroom has been made active');
      classroomsStore.getTeacherActiveClassroomsQuery().invalidateInterest();
      classroomsStore.getActiveClassroomMetadataQuery().invalidateInterest();
      classroomsStore.getTeacherArchivedClassroomsQuery().invalidateInterest();
      const {resourcePath} = getClassroomsForTeacherClassroomsDashboardQuery().done();
      invalidatePartialInterest({resourcePath});
    } catch (e) {
      this.writeData('errorMessaging', 'There was a problem making your classroom active');
    }
  }

  async _deleteClassroom(): Promise<any> {
    const classroom = this.getClassroom();
    try {
      await classroom.delete();
      this.writeData('successMessaging', 'Your classroom has been deleted');
      classroomsStore.getTeacherActiveClassroomsQuery().invalidateInterest();
      classroomsStore.getActiveClassroomMetadataQuery().invalidateInterest();
      classroomsStore.getTeacherArchivedClassroomsQuery().invalidateInterest();
      const {resourcePath} = getClassroomsForTeacherClassroomsDashboardQuery().done();
      invalidatePartialInterest({resourcePath});

      // We immediately bail upon successful deletion due to the hierarchical nature of this route
      history.pushState(null, '/classes');
    } catch (e) {
      this.writeData('errorMessaging', 'There was a problem deleting your classroom');
    }
  }

  async _saveClassroom(): Promise<any> {
    this.writeData('saveAttempted', true);

    if (!this.isValid()) {
      return;
    }

    let classroom = this.getClassroom();
    const selectedSubjects = this.getSelectedSubjects();
    const classroomSubjects = classroom.getSubjects();

    /*
       merge one by one our constituent parts, skip adding
       subjects if we are not changing what we have
    */
    classroom = classroom.setName(this.getClassroomName());

    const selectedSubjectsIds = selectedSubjects.map((subject) => subject.getId()).toSet();
    const classroomSubjectIds = classroomSubjects
      .valueSeq()
      .map((subject) => subject.getId())
      .toSet();

    if (!selectedSubjectsIds.equals(classroomSubjectIds)) {
      classroom = classroom.replaceRelationship({
        type: 'subjects_v2',
        relation: selectedSubjectsIds
      });
    }

    if (this.isClassroomSchoolEditable() === true) {
      // This is not a real school object -- only contains id and name
      const selectedSchool = this.getSchoolSelectionDropdownStore().getSelectedItem();
      classroom = classroom.updateRelationship('school_v4', selectedSchool.get('id'));
    }

    try {
      await classroom.save();
      this.writeData('successMessaging', 'Your classroom has been updated');
      classroomsStore.getTeacherActiveClassroomsQuery().invalidateInterest();
      classroomsStore.getTeacherArchivedClassroomsQuery().invalidateInterest();
      const {resourcePath} = getClassroomsForTeacherClassroomsDashboardQuery().done();
      invalidatePartialInterest({resourcePath});
    } catch (error) {
      this.writeData('errorMessaging', 'There was a problem updating your classroom');
      const hasUnapprovedCoteacherError = error?.response?.body?.errors?.some(
        (err) => err.code === 'Errors.BadRequest.ValidationError.UnapprovedCoteacher'
      );

      if (hasUnapprovedCoteacherError) {
        this.writeData('hasUnapprovedCoteacherError', true);
      }
    }
  }

  saveAttempted(): boolean {
    return this.readData('saveAttempted');
  }

  async _removeSelfFromClassroom() {
    const classroom = this.getClassroom();
    const teacherIds = classroom.getTeachers().map(teacher => teacher.getId());
    const currentTeacherId = masqueradeStore.getUserIdByMasqueradeState();
    try {
      await classroom.replaceRelationship({
        type: 'teachers_v1',
        relation: teacherIds.filter(teacherId => teacherId !== currentTeacherId)
      }).save();
      this.writeData('successMessaging', 'You have successfully been removed from this classroom.');
      invalidatePartialInterest({resourcePath: ['teachers_v1', currentTeacherId, 'classes_v1']})
      history.pushState(null, '/classes');
    } catch {
      this.writeData('errorMessaging', 'There was a problem removing yourself from this classroom.');
    }
  }

  _removeMessaging() {
    this.writeData((state) => {
      return state.set('successMessaging', '').set('errorMessaging', '');
    });
  }

  _handleSchoolAssociationChange() {
    this.writeData('hasUnapprovedCoteacherError', false);
  }

  getClassroomQuery(): QueryBuilder {
    return resource('classroom_v1')
      .mandarkEndpoint(['classrooms_v1', appStore.routerProps.params.classId])
      .include('school_v4')
      .include('teachers_v1')
      .include('subjects_v2.curriculum_area_v1');
  }

  _getTeacherSchoolQuery(): QueryBuilder {
    return (
      resource('teachers_v1')
        .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState()])
        .include('school_v4')
        /**
         * We have to make sure we're only requesting classrooms an administrator is
         * allowed to see – we do this by filtering on the school of the current
         * masquerade state.
         */
        .filter(
          {
            school_v4: masqueradeStore.getSchoolId()
          },
          masqueradeStore.isMasquerading()
        )
    );
  }

  _getTeacherPersonnelQuery(): QueryBuilder {
    return (
      resource('school_personnel_v1')
        .mandarkEndpoint(['school_personnel_v1'])
        .include('school_v4')
        .filter({
          teacher_v1: masqueradeStore.getUserIdByMasqueradeState(),
          status: 'approved'
        })
        /**
         * We have to make sure we're only requesting classrooms an administrator is
         * allowed to see – we do this by filtering on the school of the current
         * masquerade state.
         */
        .filter(
          {
            school_v4: masqueradeStore.getSchoolId()
          },
          masqueradeStore.isMasquerading()
        )
    );
  }

  getClassroom(): ClassroomModelV1 {
    return this.getClassroomQuery().getResource();
  }

  getSchoolSelectionDropdownStore(): BlockyDropdownStore {
    return this._schoolSelectionDropdownStore;
  }

  _schoolToMapConverter(school): Map<string, string> {
    return new Map({
      id: school.getId(),
      name: school.getName()
    });
  }

  getCurrentSchool(): Map<string, string> {
    const school = this.getClassroomQuery()
      .getResource()
      .getSchool();
    return this._schoolToMapConverter(school);
  }

  getAllAssociatedSchools(): Set<*> {
    const school = this.getCurrentSchool();
    if (masqueradeStore.isMasquerading()) {
      return new Set([school]);
    }

    const schoolPersonnelRecords = this._getTeacherPersonnelQuery().getResource();
    const teacherSpecifiedSchool = this._getTeacherSchoolQuery()
      .getResource()
      .getSchool();
    const trimmedTeacherSpecifiedSchool = this._schoolToMapConverter(teacherSpecifiedSchool);
    return schoolPersonnelRecords
      .map((record) => record.getSchool())
      .map(this._schoolToMapConverter)
      .concat([school, trimmedTeacherSpecifiedSchool])
      .toSet();
  }

  hasSchoolData(): boolean {
    return (
      this._getTeacherSchoolQuery().isResourcePopulated() &&
      this._getTeacherPersonnelQuery().isResourcePopulated()
    );
  }

  getClassroomName(): string {
    return this.readData('classroomName');
  }

  getSuccessMessaging(): string {
    return this.readData('successMessaging');
  }

  getErrorMessaging(): string {
    return this.readData('errorMessaging');
  }

  getFormErrors(): Map<string, *> {
    const classroomName = this.getClassroomName();
    const isNameValid = classroomName !== '' && !(classroomName.length > 40);
    let nameError = '';
    if (classroomName === '') {
      nameError = 'You must enter a classroom name';
    } else if (classroomName.length > 40) {
      nameError = 'Classroom name must not be greater than 40 characters';
    }

    return fromJS({
      name: {
        isValid: this.saveAttempted() ? isNameValid : true,
        errorMessage: nameError
      }
    });
  }

  hasNameError(): boolean {
    return this.getFormErrors().getIn(['name', 'isValid']) === false;
  }

  getNameError(): string {
    return this.getFormErrors().getIn(['name', 'errorMessage']);
  }

  hasUnapprovedCoteacherError(): boolean {
    return this.readData('hasUnapprovedCoteacherError');
  }

  isValid(): boolean {
    return this.getFormErrors().every((errors) => {
      return errors.get('isValid') === true;
    });
  }

  // Can only interact with dropdown to change school if you have more than one option.
  // Masquerading is automatically handled in getAllAssociatedSchools()
  isClassroomSchoolEditable(): boolean {
    return this.getAllAssociatedSchools().size > 1;
  }
}

const teacherClassroomSettingsStore = new TeacherClassroomSettingsStore(
  'TeacherClassroomSettingsStore'
);
export default teacherClassroomSettingsStore;
